import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { dispatch, store } from 'store';
import { fetchUnreadCounts, type ChatScope, type SystemCounts, type ViewCountCell } from 'api/unread-counts';
import { buildUnreadCountsRequest } from 'utils/build-unread-counts-request';

export type { ViewCountCell, SystemCounts } from 'api/unread-counts';

interface ViewCountsState {
  system: SystemCounts;
  /** מונה תצוגת «בוטים» — שיחות שמוחזקות כרגע אצל בוט */
  bots: ViewCountCell | null;
  custom: Record<string, ViewCountCell>;
  lastUpdatedIso: string | null;
  loading: boolean;
}

const initialState: ViewCountsState = {
  system: {},
  bots: null,
  custom: {},
  lastUpdatedIso: null,
  loading: false,
};

const viewCountsSlice = createSlice({
  name: 'viewCounts',
  initialState,
  reducers: {
    storeSetViewCounts: (
      state,
      action: PayloadAction<{
        system: SystemCounts;
        bots?: ViewCountCell | null;
        custom: Record<string, ViewCountCell>;
        lastUpdatedIso: string;
      }>,
    ) => {
      state.system = action.payload.system;
      state.bots = action.payload.bots ?? null;
      state.custom = action.payload.custom;
      state.lastUpdatedIso = action.payload.lastUpdatedIso;
    },
    storeSetViewCountsLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    storeClearViewCounts: () => initialState,
    storeDecrementViewCountUnread: (
      state,
      action: PayloadAction<{
        flowStatusCode?: number | null;
        scopes: ChatScope[];
      }>,
    ) => {
      const statusKey =
        action.payload.flowStatusCode != null ? String(action.payload.flowStatusCode) : null;
      if (!statusKey) return;

      for (const scope of action.payload.scopes) {
        const scopeCounts = state.system[scope];
        const cell = scopeCounts?.[statusKey];
        if (cell && cell.unread > 0) {
          cell.unread -= 1;
        }
      }
    },
  },
});

export default viewCountsSlice.reducer;
export const {
  storeSetViewCounts,
  storeSetViewCountsLoading,
  storeClearViewCounts,
  storeDecrementViewCountUnread,
} = viewCountsSlice.actions;

/**
 * מושך מוני תצוגות מ-Node ושומר ל-store. שגיאות לא מרוקנות את ה-state
 * (stale-but-visible). השתמש ב-scheduleUnreadCountsRefresh לרענון מאירועי socket.
 */
export const getUnreadCounts = async (): Promise<void> => {
  const req = buildUnreadCountsRequest(store.getState());
  if (!req) return; // המשתמש עוד לא מזוהה — אין מה לבקש

  dispatch(storeSetViewCountsLoading(true));
  try {
    const data = await fetchUnreadCounts(req);
    if (data && data.system) {
      dispatch(
        storeSetViewCounts({
          system: data.system,
          bots: data.bots ?? null,
          custom: data.custom || {},
          lastUpdatedIso: data.meta?.computedAtIso || new Date().toISOString(),
        }),
      );
    }
  } finally {
    dispatch(storeSetViewCountsLoading(false));
  }
};
